// Who can join this trip.
//
// Was the share-code panel: a link with a code baked in, plus the code in big
// type to read down the phone. The code is gone (#34) — it was a bearer token
// that let whoever held it join as any household. Membership binds to an email
// address now, so this panel manages the list of addresses instead.
//
// Organizer-only, and enforced twice: the UI hides the controls, and the
// `trip_invite` RLS policies reject writes from anyone whose membership.role is
// not 'organizer'. The UI half is a convenience; the policy is the boundary.
//
// An address also has to be pointed at a household (#50). It used to be enough
// to be on this list — you then chose your own household at /join, which meant
// anyone here could walk into any family's intake. The household column has
// existed since the allowlist landed but nothing ever set it; this panel is what
// sets it. An address with no household is a half-finished invitation: that
// person can sign in and will be told to come back once an organizer has
// finished, so unassigned rows are called out rather than left looking normal.
import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from 'react'
import { supabase } from '../lib/supabaseClient'

export interface InvitePanelProps {
  tripId: string
  /** Hides the add/remove controls. The RLS policy is what actually stops a member. */
  isOrganizer: boolean
  /** The trip's households, to point invitations at. Owned by the parent screen. */
  households: ReadonlyArray<{ id: string; label: string }>
}

interface InviteRow {
  email: string
  created_at: string
  household_id: string | null
}

const css = `
.spz-invite { border: 1px solid color-mix(in srgb, currentColor 15%, transparent); border-radius: 0.75rem; padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
.spz-invite h2 { font-size: 1.05rem; margin: 0; }
.spz-invite .spz-invite-hint { color: var(--muted); font-size: 0.85rem; }
.spz-invite .spz-invite-link { width: 100%; min-height: 2.75rem; padding: 0.6rem 0.75rem; font: inherit; font-size: 0.9rem; border-radius: 0.5rem; border: 1px solid color-mix(in srgb, currentColor 25%, transparent); background: transparent; color: inherit; }
.spz-invite button { min-height: 2.75rem; padding: 0.6rem 1rem; font: inherit; font-weight: 600; border-radius: 0.5rem; border: 1px solid var(--accent); background: var(--accent); color: var(--ground); cursor: pointer; }
.spz-invite button:disabled { opacity: 0.6; cursor: default; }
.spz-invite .spz-invite-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
.spz-invite .spz-invite-list li { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; flex-wrap: wrap; padding: 0.55rem 0; border-bottom: 1px dashed color-mix(in srgb, currentColor 15%, transparent); font-size: 0.92rem; word-break: break-all; }
.spz-invite .spz-invite-email { flex: 1 1 11rem; min-width: 0; }
.spz-invite .spz-invite-remove { min-height: 2rem; padding: 0.2rem 0.6rem; font-size: 0.8rem; font-weight: 600; background: transparent; color: var(--muted); border-color: color-mix(in srgb, currentColor 25%, transparent); flex: none; }
.spz-invite select { flex: 1 1 10rem; min-width: 0; min-height: 2.5rem; padding: 0.4rem 0.5rem; font: inherit; font-size: 0.9rem; border-radius: 0.5rem; border: 1px solid color-mix(in srgb, currentColor 25%, transparent); background: transparent; color: inherit; }
/* Unassigned is a blocking state, not a cosmetic gap — that person cannot join. */
.spz-invite select.spz-invite-unassigned { border-color: #b3261e; border-width: 2px; color: #b3261e; font-weight: 600; }
.spz-invite .spz-invite-household { color: var(--muted); font-size: 0.85rem; flex: 1 1 10rem; }
.spz-invite form { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.spz-invite form input { flex: 1 1 14rem; min-width: 0; min-height: 2.75rem; padding: 0.6rem 0.75rem; font: inherit; font-size: 1rem; border-radius: 0.5rem; border: 1px solid color-mix(in srgb, currentColor 25%, transparent); background: transparent; color: inherit; }
.spz-invite .spz-invite-error { color: #b3261e; font-size: 0.85rem; font-weight: 600; }
`

export default function InvitePanel({
  tripId,
  isOrganizer,
  households,
}: InvitePanelProps): JSX.Element {
  const [invites, setInvites] = useState<InviteRow[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [draftHousehold, setDraftHousehold] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const link = `${window.location.origin}/join`

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('trip_invite')
      .select('email, created_at, household_id')
      .eq('trip_id', tripId)
      .order('email', { ascending: true })
    setInvites(err ? [] : ((data ?? []) as InviteRow[]))
    setLoading(false)
  }, [tripId])

  useEffect(() => {
    void load()
  }, [load])

  async function add(event: FormEvent): Promise<void> {
    event.preventDefault()
    // Lower-cased to match the column's CHECK and the lookup in the join function.
    const email = draft.trim().toLowerCase()
    if (!email) return
    if (!email.includes('@') || email.startsWith('@')) {
      setError("That doesn't look like an email address.")
      return
    }

    setBusy(true)
    setError(null)
    const { error: err } = await supabase.from('trip_invite').insert({
      trip_id: tripId,
      email,
      // Empty means "not yet" — stored as null, and the row shows up flagged.
      household_id: draftHousehold || null,
    })
    setBusy(false)

    if (err) {
      // 23505 is the (trip_id, email) primary key — already invited, not a failure
      // worth alarming anyone about.
      setError(
        err.code === '23505'
          ? 'That address is already on the list.'
          : 'Could not add that address. You may not be an organizer on this trip.',
      )
      return
    }
    setDraft('')
    setDraftHousehold('')
    await load()
  }

  /**
   * Point an invitation at a household, or clear it.
   *
   * Reassigning does not move anyone who has already joined — `membership` is a
   * separate row. The join function repoints it when they next hit /join, but an
   * organizer fixing a mistake for someone already inside should move the
   * membership on the Households list too.
   */
  async function assign(email: string, householdId: string): Promise<void> {
    setBusy(true)
    setError(null)
    const { error: err } = await supabase
      .from('trip_invite')
      .update({ household_id: householdId || null })
      .eq('trip_id', tripId)
      .eq('email', email)
    setBusy(false)
    if (err) {
      setError('Could not change that household. You may not be an organizer on this trip.')
      return
    }
    await load()
  }

  async function remove(email: string): Promise<void> {
    setBusy(true)
    setError(null)
    const { error: err } = await supabase
      .from('trip_invite')
      .delete()
      .eq('trip_id', tripId)
      .eq('email', email)
    setBusy(false)
    if (err) {
      setError('Could not remove that address.')
      return
    }
    await load()
  }

  async function copy(): Promise<void> {
    setCopyFailed(false)
    try {
      // Unavailable on non-HTTPS local dev — fall back to selecting the field.
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable')
      await navigator.clipboard.writeText(link)
      setCopied(true)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyFailed(true)
      inputRef.current?.select()
    }
  }

  return (
    <section className="spz-invite" aria-labelledby="spz-invite-title">
      <style>{css}</style>
      <h2 id="spz-invite-title">Who can join</h2>
      <p className="spz-invite-hint">
        Anyone on this list can sign in with that address and join. Nobody else can, and there is
        no code to pass around. Each address also needs a household &mdash; that&rsquo;s the
        household they fill in, and they can&rsquo;t choose it themselves.
      </p>
      {isOrganizer && households.length === 0 && (
        <p className="spz-invite-error" role="alert">
          Add a household below before inviting anyone &mdash; there&rsquo;s nothing to assign
          addresses to yet, so nobody would be able to join.
        </p>
      )}

      <input
        ref={inputRef}
        className="spz-invite-link"
        type="text"
        readOnly
        value={link}
        aria-label="Sign-in link"
        onFocus={(e) => e.currentTarget.select()}
      />
      <button type="button" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy sign-in link'}
      </button>
      {copyFailed && (
        <p className="spz-invite-hint" role="status">
          Copying isn&rsquo;t available here &mdash; the link above is selected, copy it manually.
        </p>
      )}

      {loading ? (
        <p className="spz-invite-hint">Loading the list&hellip;</p>
      ) : (
        <ul className="spz-invite-list">
          {invites.length === 0 && (
            <li>
              <span className="spz-invite-hint">
                Nobody is on the list yet, so nobody can join.
              </span>
            </li>
          )}
          {invites.map((invite) => (
            <li key={invite.email}>
              <span className="spz-invite-email">{invite.email}</span>
              {isOrganizer ? (
                <select
                  className={invite.household_id ? undefined : 'spz-invite-unassigned'}
                  value={invite.household_id ?? ''}
                  disabled={busy}
                  aria-label={`Household for ${invite.email}`}
                  onChange={(e) => void assign(invite.email, e.target.value)}
                >
                  <option value="">Not assigned — can&rsquo;t join</option>
                  {households.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="spz-invite-household">
                  {households.find((h) => h.id === invite.household_id)?.label ?? 'Not assigned'}
                </span>
              )}
              {isOrganizer && (
                <button
                  type="button"
                  className="spz-invite-remove"
                  onClick={() => void remove(invite.email)}
                  disabled={busy}
                  aria-label={`Remove ${invite.email}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOrganizer && (
        <form onSubmit={(e) => void add(e)}>
          <input
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="cousin@example.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Email address to invite"
          />
          <select
            value={draftHousehold}
            onChange={(e) => setDraftHousehold(e.target.value)}
            aria-label="Household for the new address"
          >
            <option value="">Which household?</option>
            {households.map((h) => (
              <option key={h.id} value={h.id}>
                {h.label}
              </option>
            ))}
          </select>
          <button type="submit" disabled={busy || draft.trim().length === 0}>
            Add
          </button>
        </form>
      )}

      {error && (
        <p className="spz-invite-error" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
